import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { MilestoneItems_cardsCountTask } from './__generated__/index.graphql';

const Root = styled.div`
  width: 100%;
`;
const MilestoneItem = styled(Vertical).attrs({ center: true, gap: 1.5 })`
  padding: var(--intermediate-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  background: var(--c-nl-200);

  &.completed {
    opacity: 0.5;
    height: 100%;
  }
`;
const ScoreWrapper = styled.div`
  align-self: flex-end;
`;
const Wrapper = styled(Vertical).attrs({ center: true })`
  width: 100%;
`;
const ProgressWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  width: 100%;
`;

type Props = {
  cardsCountTasks: MilestoneItems_cardsCountTask[];
};
export const MilestoneItems = ({ cardsCountTasks }: Props) => {
  const nextMilestone = cardsCountTasks.find(
    ({ scoreToReach, liveScore }) => liveScore < scoreToReach
  );
  const milestone = nextMilestone || cardsCountTasks.at(0);
  const { thresholds, liveScore } = milestone || {};

  const indexOfCurrentThreshold = milestone?.thresholds.findIndex(
    t => t.current
  );

  return (
    <Root>
      <Scrollable
        itemToDisplay={1}
        indexToScroll={indexOfCurrentThreshold}
        overhang={8}
        contained
      >
        {thresholds?.map((threshold, index) => {
          const { count, rewardConfigs } = threshold;
          const completed = (liveScore || 0) >= count;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <MilestoneItem key={index} className={classNames({ completed })}>
              <LabelM bold>
                {completed ? (
                  <FormattedMessage {...glossary.completed} />
                ) : (
                  <FormattedMessage
                    id="MilestoneItems.title"
                    defaultMessage="Milestone {nb, number}"
                    values={{ nb: index + 1 }}
                  />
                )}
              </LabelM>
              <Wrapper>
                <Horizontal>
                  {rewardConfigs.map(rewardConfig => {
                    return (
                      <AnyRewardConfig
                        key={rewardConfig.id}
                        anyRewardConfig={rewardConfig}
                        variant={Variant.INLINE_CHIP}
                      />
                    );
                  })}
                </Horizontal>
                {!completed && (
                  <ProgressWrapper>
                    <Progress
                      value={liveScore || 0}
                      max={count}
                      color="linear-gradient(to right, var(--c-yellow-500), var(--c-yellow-300))"
                      bgColor="var(--c-nd-200)"
                    />
                    <ScoreWrapper>
                      <LabelS bold as="span">
                        {liveScore}
                      </LabelS>
                      <LabelS bold color="var(--c-nd-600)" as="span">
                        {' / '}
                        {count}
                      </LabelS>
                    </ScoreWrapper>
                  </ProgressWrapper>
                )}
              </Wrapper>
            </MilestoneItem>
          );
        })}
      </Scrollable>
    </Root>
  );
};

MilestoneItems.fragments = {
  cardsCountTask: gql`
    fragment MilestoneItems_cardsCountTask on CardsCountTask {
      id
      liveScore
      scoreToReach
      thresholds {
        current
        count
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<MilestoneItems_cardsCountTask>,
};
